import React from 'react';

interface UseSignItemsProps {
  initialValues: boolean[];
  checkAnimationMs?: number;
}

const DEFAULT_DELAY_MS = 400;

export const useSignItems = ({ initialValues, checkAnimationMs = 800 }: UseSignItemsProps) => {
  const [values, setValues] = React.useState<boolean[]>(initialValues);
  const [isSigned, setIsSigned] = React.useState(false);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const notCheckedItemCount = values.filter((v) => !v).length;

  const toggleItem = (key: number) => {
    setValues((prev) => prev.map((v, i) => (i === key ? !v : v)));
  };

  const resetSign = React.useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsSigned(false);
    setValues(initialValues);
  }, [initialValues]);

  const sign = ({ onSigned }: { onSigned: () => void }) => {
    if (isSigned) {
      return;
    }

    setIsSigned(true);

    timeoutRef.current = setTimeout(
      () => {
        onSigned();
      },
      DEFAULT_DELAY_MS + checkAnimationMs * notCheckedItemCount
    );
  };

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const delays = React.useMemo(() => {
    return values.reduce((acc, v, i, arr) => {
      if (v) {
        return [...acc, 0];
      }

      const delay = DEFAULT_DELAY_MS + checkAnimationMs * arr.slice(0, i).filter((item) => !item).length;

      return [...acc, delay];
    }, [] as number[]);
  }, [values, checkAnimationMs]);

  return { values, toggleItem, isSigned, sign, resetSign, delays };
};
